import { useTheme } from '@emotion/react'
import { Box, Divider, Skeleton, Typography, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { makeStyles } from '@mui/styles'
import { WhiteBox, WhiteBoxHeader } from 'components/AppShell'
import Chip from 'components/Chip'
import CustomButton from 'components/CustomButton'
import { NoResultComponent } from 'components/Home/TodoList'
import Pagination from 'components/Pagination'
import { useServices } from 'contexts'
import React from 'react'
import { useEffect } from 'react'
import { useState } from 'react'
import { getResumeAnalysisHistory } from 'services/shortlistingService'
import { Session } from 'utils'
import { useNavigateWithClient } from 'hooks'

const useStyles = makeStyles((theme) => ({
  box : {
    display : 'flex',
    flexDirection : 'column',
    width : '100%',
    gap : theme.spacing(5),
    marginTop : theme.spacing(5),
    maxHeight : '405px',
    overflow : 'auto',
    height : '405px',
    paddingRight : theme.spacing(3),
    overflowX: 'hidden'
  },
  cardContainer: {
    display: 'flex',
    borderRadius: theme.spacing(2),
    width : '100%',
      border: `2px solid ${theme.palette.neutral['clr-200']}`,
      // '&:hover': {
      //   border: `2px solid ${colors.hoverColor}`,
      // }
  },
  category : {
    width : '100px',
    height : '120px',
    backgroundColor : theme.palette.success['clr-100'],
    borderRadius: theme.spacing(2,0,0,2),
    display : 'flex',
    justifyContent : 'center',
    alignItems : 'center'
  },
  content : {
    display : 'flex',
    justifyContent : 'space-between',
    alignItems : 'center',
    padding : theme.spacing(4,8),
    width : '100%'
  },
  bottomContainer : {
    padding: theme.spacing(2), 
    display:"flex",
    justifyContent:"space-between",
    alignItems : 'center',
    marginTop : theme.spacing(2),
    background: `linear-gradient(360deg, #FFFFFF 80%, rgba(255, 255, 255, 0) 100%)`,
    position : 'relative',
    bottom : '0px',
  },
  dialogPaper: {
    maxWidth: '800px',
    width: '90vw',
    maxHeight: '80vh',
    padding : theme.spacing(10)
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dialogContent: {
    minHeight: '400px',
  },
  jobDescriptionText: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.neutral['clr-300']}`,
    maxHeight: '500px',
    overflow: 'auto',
  },
  pdfContainer: {
    width: '100%',
    height: '500px',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
  },
  pdfEmbed: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  clickableChip: {
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  pdfContainer: {
    width: '100%',
    height: 'calc(90vh - 64px)', // Adjust if your header is 64px
    overflow: 'hidden',
  },
  iframePreview: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  
}))

// Job Description Dialog Component
const JobDescriptionDialog = ({ open, onClose, jobDescription }) => {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      classes={{ paper: classes.dialogPaper }}
      maxWidth={false}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6-medium" color="primary.main">
          Job description
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.jobDescriptionText}>
          <Typography variant="body01-medium" color='neutral.clr-900'>
            {jobDescription || 'No job description available'}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const ResumeDialog = ({ open, onClose, resumePath }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [iframeError, setIframeError] = useState(false);

  const fullResumeUrl = resumePath ? `https://assets.languify.in/${resumePath.replace(/^\/?/, '')}` : null;


  const handleOpenInNewTab = () => {
    if (fullResumeUrl) {
      window.open(fullResumeUrl, '_blank');
    }
  };

  const handleDownload = () => {
    if (fullResumeUrl) {
      const link = document.createElement('a');
      link.href = fullResumeUrl;
      link.download = 'resume.pdf';
      link.click();
    }
  };

  useEffect(() => {
    if (open) {
      setIframeError(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth={false}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6-medium" color="primary.main">
          Resume
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.pdfContainer}>
          {fullResumeUrl ? (
            !iframeError ? (
              <iframe
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(fullResumeUrl)}&embedded=true`}
              className={classes.iframePreview}
              title="Resume PDF"
              allow="fullscreen"
              frameBorder="0"
              onError={() => setIframeError(true)}
            />
            
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                height="100%"
                gap={2}
              >
                <Typography variant="h6" color="text.secondary">
                  PDF Preview Not Available
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  The PDF cannot be displayed. You can open it in a new tab or download it.
                </Typography>
                <Box display="flex" gap={2} mt={2}>
                  <CustomButton onClick={handleOpenInNewTab}>
                    Open in New Tab
                  </CustomButton>
                  <CustomButton onClick={handleDownload}>
                    Download PDF
                  </CustomButton>
                </Box>
              </Box>
            )
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              color="text.secondary"
            >
              <Typography>No resume available</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const ResumeCard = ({key , resume}) => {
  const classes = useStyles();
  const theme = useTheme();
  const navigateWithClient = useNavigateWithClient();

  const [jobDescDialogOpen, setJobDescDialogOpen] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  
  const getScoreColor = (score) => {
    if (score >= 0 && score < 50) {
      return 'danger.clr-500';
    } else if (score >= 50 && score < 80) {
      return 'warning.clr-500';
    } else if (score >= 80 && score <= 100) {
      return 'success.clr-500';
    }
    return 'neutral.clr-500'; // fallback color
  };

  const handleJobDescriptionClick = () => {
    if (resume.jobDescription) {
      setJobDescDialogOpen(true);
    }
  };

  const handleResumeClick = () => {
    if (resume.resume?.path) {
      setResumeDialogOpen(true);
    }
  };

  return (
    <>
      <Box key={key} className={classes.cardContainer}>
          <Box className={classes.content}>
            <Box display='flex' flexDirection='column' gap='12px'>
              <Typography variant='h5-medium'>{resume?.jobRole || resume?.shortlistTask?.jobRole}</Typography>
              <Box display='flex' gap='8px'>
                 <Chip
                content={resume?.shortlistTask ? 'Shortlisting task' : 'Self practiceshort'}
                  color={theme.palette.neutral['clr-600']}
                  bgColor={theme.palette.tertiary['clr-100']}
                  sx={{ padding: theme.spacing(1, 2), borderRadius: '4px' }}
                />
                 <Chip
                  content={resume?.company ?  "Company specific" : "Role specific"}
                  color={theme.palette.neutral['clr-600']}
                  bgColor={theme.palette.tertiary['clr-100']}
                  sx={{ padding: theme.spacing(1, 2), borderRadius: '4px' }}
                />
                <Divider orientation="vertical" flexItem />
                {resume.jobDescription && (
                  <Chip
                    content={"Job description"}
                    color={theme.palette.primary.main}
                    bgColor={theme.palette.tertiary['clr-100']} 
                    typographyVariant='body01-link'
                    sx={{ 
                      padding: theme.spacing(1, 2), 
                      borderRadius: '4px',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      }
                    }}
                    onClick={handleJobDescriptionClick}
                  />
                )}
                {resume.resume?.path && (
                  <Chip
                    content={"Resume"}
                    color={theme.palette.primary.main}
                    bgColor={theme.palette.tertiary['clr-100']} 
                    typographyVariant='body01-link'
                    sx={{ 
                      padding: theme.spacing(1, 2), 
                      borderRadius: '4px',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      }
                    }}
                    onClick={handleResumeClick}
                  />
                )}
               
              </Box>
              <Box display='flex' gap='8px'>
                <Box display='flex' gap='4px'>
                  <Typography variant='body01-bold'>Relevance score: </Typography>
                  <Typography variant='body01-bold' color={getScoreColor(resume?.score)}>{resume?.score}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box display='flex' gap='4px'>
                  <Typography variant='body01-bold'>Skills match score:</Typography>
                  <Typography variant='body01-bold' color={getScoreColor(resume?.analysis?.skillMatchScore)}>{resume?.analysis?.skillMatchScore}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box display='flex' gap='4px'>
                  <Typography variant='body01-bold'>Experience match score:</Typography>
                  <Typography variant='body01-bold' color={getScoreColor(resume?.analysis?.relevantExperienceScore)}>{resume?.analysis?.relevantExperienceScore}</Typography>
                </Box>
              </Box>
            </Box>
            <CustomButton
              onClick={() => {
                navigateWithClient(`/performance/shortlist-genie/${resume?._id}`)
              }}
            >
              View feedback
            </CustomButton>
          </Box>
      </Box>

      {/* Job Description Dialog */}
      <JobDescriptionDialog
        open={jobDescDialogOpen}
        onClose={() => setJobDescDialogOpen(false)}
        jobDescription={resume.jobDescription}
      />

      {/* Resume Dialog */}
      <ResumeDialog
        open={resumeDialogOpen}
        onClose={() => setResumeDialogOpen(false)}
        resumePath={resume.resume?.path}
      />
    </>
  )
}

const SkeletonCard = () => {
  const theme = useTheme();
  const classes = useStyles();

  return (
    <Box className={classes.cardContainer} sx={{ height: '142px' }}>
      <Box className={classes.content}>
        <Box display="flex" flexDirection="column" gap={theme.spacing(2)} sx={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" height={28} />
          <Box display="flex" gap={1} alignItems="center">
            <Skeleton variant="rectangular" width={130} height={28} sx={{ borderRadius: '4px' }} />
            <Skeleton variant="rectangular" width={130} height={28} sx={{ borderRadius: '4px' }} />
            <Divider orientation="vertical" flexItem />
            <Skeleton variant="rectangular" width={100} height={28} sx={{ borderRadius: '4px' }} />
          </Box>
          <Box display="flex" gap={2} alignItems="center">
            <Skeleton variant="text" width={160} height={20} />
            <Divider orientation="vertical" flexItem />
            <Skeleton variant="text" width={180} height={20} />
            <Divider orientation="vertical" flexItem />
            <Skeleton variant="text" width={200} height={20} />
          </Box>
        </Box>
        <Skeleton variant="rectangular" width={160} height={36} sx={{ borderRadius: '8px' }} />
      </Box>
    </Box>
  );
};

const ResumeHistory = ({refresh}) => {
  const theme = useTheme();
  const classes = useStyles();
  const service = useServices();

  const [loading , setLoading] = useState(false)
  const [resumes , setResumes] = useState([])
  const [page, setPage] = useState(0);
  const [total , setTotal] = useState(0)
  const pageSize = 9

    useEffect(() => {
      const fetchHistory = async () => {
        try {
          setLoading(true);
          let filters = {
            user : Session.userId,
            order: "desc",
            orderBy: "createdAt",
            pageNumber: page + 1,
            pageSize: pageSize,
            includeTotal: true,
          }
          const analysisHistory = await service.callService(getResumeAnalysisHistory , filters);
          setResumes(analysisHistory.resumeInsights)
          setTotal(analysisHistory.total)
          
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
        }
      };
  
      fetchHistory();
    }, [page , refresh]);

  return (
     <WhiteBox sx={{ position: 'relative', marginTop: theme.spacing(4) }}>
              <WhiteBoxHeader
                id='to-do-list'
                heading="Resume analysis history"
                sx={{ marginBottom: theme.spacing(4) }}
                subheading='Check your past analysis scores, uploaded resumes and Job descriptions'
              >
               
              </WhiteBoxHeader>
              
              <Divider />

               <Box className={classes.box}>
                      {
                        loading 
                        ? Array.from({ length: 3 }).map((_, i) => (
                          <SkeletonCard key={i} />
                        ))        
                        :
                          resumes?.length ? (
                            resumes.map((resume, index) => (
                              <ResumeCard 
                                key={index} 
                                resume={resume}
                              />
                            ))
                          ) : (
                            <NoResultComponent
                              description='No history'
                              image='https://assets.languify.in/images/noPreparation.png'
                            />
                          )
                      }
                    </Box>
                     {resumes.length ?
                        <Box className={classes.bottomContainer}>
                          <Typography variant='body01-bold' color='neutral.clr-900'>
                            {resumes.length > 0 
                              ? `Attempts: ${page * pageSize + 1}-${Math.min((page + 1) * pageSize, total)} of ${total}` 
                              : 'No attempts'
                            }
                          </Typography>
                          <Pagination
                            count={Math.max(1, Math.ceil(total / pageSize))}
                            page={page}
                            setPage={setPage}
                          />
                        </Box> 
                     :null}
      </WhiteBox>
  )
}

export default ResumeHistory